import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { useMemo } from "react";

import { useGetAccessorials } from "../../../hooks/use-get-accessorials.ts";
import { useShipmentAccessorialsDraft } from "../../../hooks/use-shipment-draft.ts";

const SHIPMENT_ACCESSORIAL_NAMES = [
	"Blind Single",
	"Blind Double",
	"In Bond",
	"Protect From Freezing",
];

/**
 * Shipment-level accessorial checkboxes (edit mode) or the read-only list of the
 * currently selected names (view mode).
 */
export const ShipmentAccessorials = ({
	accessorials,
	isEditing,
}: {
	accessorials?: string[];
	isEditing: boolean;
}) => {
	const { data: accessorialData } = useGetAccessorials();
	const { accessorials: draftAccessorials, updateAccessorials } =
		useShipmentAccessorialsDraft();

	const selectedAccessorialNames =
		(isEditing ? (draftAccessorials ?? accessorials) : accessorials) ?? [];
	// Normalize to lowercase for case-insensitive matching against API names
	const selectedAccessorialNamesLower = new Set(
		selectedAccessorialNames.map((name) => name.toLowerCase()),
	);

	const shipmentAccessorialOptions = useMemo(
		() =>
			SHIPMENT_ACCESSORIAL_NAMES.map((name) =>
				(accessorialData?.allAccessorials ?? []).find(
					(accessorial) => accessorial.name === name,
				),
			).filter(
				(accessorial): accessorial is NonNullable<typeof accessorial> =>
					accessorial != null,
			),
		[accessorialData?.allAccessorials],
	);

	const selectedShipmentAccessorialNames = selectedAccessorialNames.filter(
		(name) =>
			SHIPMENT_ACCESSORIAL_NAMES.some(
				(shipmentName) => shipmentName.toLowerCase() === name.toLowerCase(),
			),
	);

	const setShipmentAccessorialChecked = (name: string, checked: boolean) => {
		if (!isEditing) {
			return;
		}
		const current = draftAccessorials ?? accessorials ?? [];
		let next = current.filter((accessorial) => accessorial !== name);
		if (checked) {
			next = current.includes(name) ? current : [...current, name];
		}
		updateAccessorials(next);
	};

	const editContent =
		shipmentAccessorialOptions.length > 0 ? (
			<div className={"accessorials-columns"}>
				{shipmentAccessorialOptions.map((accessorial) => (
					<div key={accessorial.id} className={"freight-checkbox"}>
						<Checkbox
							checked={selectedAccessorialNamesLower.has(
								accessorial.name.toLowerCase(),
							)}
							id={`shipment-accessorial-${accessorial.id}`}
							onChange={(event) =>
								setShipmentAccessorialChecked(accessorial.name, event.value)
							}
						/>
						<label htmlFor={`shipment-accessorial-${accessorial.id}`}>
							{accessorial.name}
						</label>
					</div>
				))}
			</div>
		) : (
			<div className={"freight-value"}>N/A</div>
		);

	const viewContent =
		selectedShipmentAccessorialNames.length > 0 ? (
			<div className={"accessorials-text"}>
				{selectedShipmentAccessorialNames.map((name) => (
					<div key={name} data-testid={"reference-accessorial"}>
						{name}
					</div>
				))}
			</div>
		) : (
			<div className={"freight-value"}>N/A</div>
		);

	return (
		<div className={"freight-accessorials"}>
			<div className={"section-label"}>Selected Accessorials</div>
			<div className={"freight-value"}>
				{isEditing ? editContent : viewContent}
			</div>
		</div>
	);
};
